import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Clock, 
  BookOpen, 
  Trophy, 
  Users, 
  ArrowRight,
  Play,
  Target
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const HomeContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: var(--gradient-primary);
  color: white;
  padding: var(--spacing-2xl) 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: var(--spacing-xl) 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: var(--spacing-lg);
  line-height: 1.1;
  max-width: 100%;
  overflow-wrap: anywhere;
  
  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 7vw, 2.2rem);
  }
  
  .highlight {
    background: linear-gradient(45deg, #ffd700, #ffed4e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .title-tail {
    display: inline;
  }

  @media (max-width: 768px) {
    .title-tail {
      display: block;
      margin-top: var(--spacing-xs);
    }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: var(--spacing-2xl);
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .subtitle-line {
    display: inline;
  }

  @media (max-width: 768px) {
    .subtitle-line {
      display: block;
    }
  }
`;

const HeroActions = styled(motion.div)`
  display: flex;
  gap: var(--spacing-lg);
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-md);
  }
`;

const HeroButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  font-size: 1.125rem;
  font-weight: 600;
  text-decoration: none;
  transition: all var(--animation-duration) ease;
  min-height: 56px;

  @media (max-width: 768px) {
    justify-content: center;
    width: min(100%, 240px);
  }
  
  &.primary {
    background: white;
    color: var(--color-primary);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
      text-decoration: none;
    }
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-3px);
      text-decoration: none;
    }
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.section`
  padding: var(--spacing-2xl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: var(--spacing-lg);
  color: var(--color-text-primary);
  position: relative;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  text-align: center;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-2xl);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .mobile-line {
    display: inline;
  }

  @media (max-width: 768px) {
    .mobile-line {
      display: block;
    }
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-2xl);
`;

const FeatureCard = styled(motion.div)`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  text-align: center;
  transition: all var(--animation-duration) ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: var(--color-primary);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform var(--animation-duration) ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-lg);
  color: white;
  
  svg {
    width: 40px;
    height: 40px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--color-text-primary);
`;

const FeatureDescription = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
`;

const FeatureLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-primary);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    color: var(--color-secondary);
    transform: translateX(4px);
    text-decoration: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const CTASection = styled.section`
  background: var(--gradient-primary);
  color: white;
  padding: var(--spacing-2xl) 0;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: var(--spacing-2xl);
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const Home = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Clock,
      title: '历史时间线',
      description: '通过可视化时间线，直观了解各朝代的兴衰更迭，感受历史脉络。',
      link: '/timeline',
      linkText: '探索时间线'
    },
    {
      icon: BookOpen,
      title: '知识测验',
      description: '丰富的测验题目，帮助您检验和巩固历史知识，让学习更有趣味。',
      link: '/quiz',
      linkText: '开始测验'
    },
    {
      icon: Trophy,
      title: '排行榜',
      description: '与其他学习者比拼，查看您的学习成绩在全站的排名情况。',
      link: '/leaderboard',
      linkText: '查看排行'
    },
    {
      icon: Target,
      title: '个性化学习',
      description: '根据您的学习进度和兴趣，推荐最适合的学习内容和路径。',
      link: isAuthenticated ? '/profile' : '/register',
      linkText: isAuthenticated ? '个人中心' : '立即注册'
    }
  ];

  // 已移除平台相关统计数据展示

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            探索<span className="highlight">中华五千年</span><span className="title-tail">历史文明</span>
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="subtitle-line">通过现代化的可视化技术，</span>
            <span className="subtitle-line">让历史学习变得生动有趣。</span>
            <span className="subtitle-line">从夏朝到清朝，</span>
            <span className="subtitle-line">感受中华文明的博大精深。</span>
          </HeroSubtitle>
          
          <HeroActions
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <HeroButton to="/timeline" className="primary">
              <Play />
              开始探索
            </HeroButton>
            
            <HeroButton to="/quiz" className="secondary">
              <BookOpen />
              知识测验
            </HeroButton>
          </HeroActions>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle>核心功能</SectionTitle>
          <SectionSubtitle>
            我们提供多种学习方式，<span className="mobile-line">让您全方位了解历史脉络</span>
          </SectionSubtitle>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <FeatureIcon>
                  <feature.icon />
                </FeatureIcon>
                
                <FeatureTitle>{feature.title}</FeatureTitle>
                
                <FeatureDescription>
                  {feature.description}
                </FeatureDescription>
                
                <FeatureLink to={feature.link}>
                  {feature.linkText}
                  <ArrowRight />
                </FeatureLink>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </Section>

      {/* 已移除“平台数据”区块 */}

      <CTASection>
        <Container>
          <CTATitle>开始您的历史学习之旅</CTATitle>
          <CTADescription>
            加入我们，与千万学习者一起探索中华文明的璀璨历史
          </CTADescription>
          
          <HeroActions>
            {!isAuthenticated ? (
              <>
                <HeroButton to="/register" className="primary">
                  <Users />
                  立即注册
                </HeroButton>
                
                <HeroButton to="/login" className="secondary">
                  已有账户？登录
                </HeroButton>
              </>
            ) : (
              <>
                <HeroButton to="/timeline" className="primary">
                  <Clock />
                  探索时间线
                </HeroButton>
                
                <HeroButton to="/quiz" className="secondary">
                  <BookOpen />
                  开始测验
                </HeroButton>
              </>
            )}
          </HeroActions>
        </Container>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;
